/* tslint:disable:import-spacing no-inferrable-types variable-name align no-trailing-whitespace */

import {
  isarray,
  isset
} from '@/util/util';

/**
 *
 * @class ISchema
 *
 * @brief base class/helper for schema definition in our JSON format we use for representing
 * scenarios, train movements etc.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      June 2020
 *
 */
export class SchemaSpecification {

  /** the specification for what this object looks like */
  public jsonSpec: string = '';

  /** the cached object version of the schema */
  public schemaObj: any = null;

  /** the last error for trying to work with the schema of this object */
  public lastError: any = '';

  /** some objects can generate multiple data rows, they have to flag multiRow to true */
  public multiRow: boolean = false;

  /** standard constructor */
  constructor() {

  }

  /** helper to setup the initial schema based on the sub-classes specification */
  public cache(): boolean {

    if (this.schemaObj !== null) {

      /* already cached */

      return true;
    }

    this.schemaObj = this.asObject();

    if (this.schemaObj === null) {

      /* problems parsing their JSON */

      this.lastError = `problems parsing schema JSON: ${this.lastError}`;

      return false;
    }

    return true;
  }

  /** recursively search object for the object (value) tied to the given field name */
  public findObject(field: string, object: any): any {

    let result = null;

    if (object instanceof Array) {

      for ( const item of object) {

        result = this.findObject(field, item);

        if (result) {

          break;
        }
      }

    } else {

      for (const prop of Object.keys(object)) {

        if (prop === field) {

          return object[prop];
        }

        if (prop === 'fields') {

          /* don't look inside the fields section, its too far in */

          return null;
        }

        if (object[prop] instanceof Object || object[prop] instanceof Array) {

          result = this.findObject(field, object[prop]);

          if (result) {

            break;
          }
        }
      }
    }

    return result;
  }

  /** try to auto-find the embedded 'schema' object, and return it.  */
  public schema() {

    if ( !this.cache()) {

      this.lastError = `(schema) problems parsing JSON specification: ${this.lastError}`;

      /** unusable JSON specification? */

      return null;
    }

    return this.findObject('schema', this.schemaObj);
  }

  /** try to auto-find the embedded 'data' object, and return it */
  public data() {

    if ( !this.cache()) {

      this.lastError = `(data) problems parsing JSON specification: ${this.lastError}`;

      /** unusable JSON specification? */

      return null;
    }

    return this.findObject('data', this.schemaObj);
  }

  /** helper to convert this specification into a object we can actually use */
  public asObject() {

    let obj: any = null;

    try {

      obj = JSON.parse(this.jsonSpec.trim());

    } catch (e) {

      this.lastError = e;

      obj = null;
    }

    /* pass back whatever we have */

    return obj;
  }

  /** fetch the mapping of fields to data table columns */
  public columnMap(): Map<string, number> {

    if ( !this.cache()) {

      this.lastError = `(columnMap) problems parsing JSON specification: ${this.lastError}`;

      /** unusable JSON specification? */

      return null;
    }

    const  map: Map<string, number> = new Map<string, number>();
    const spec: any                 = this.schema();

    if (spec === null) {

      /* can't find the schema section, or their JSON spec is bad */

      this.lastError = `(columnMap) can't find 'schema' in JSON: ${this.lastError}`;
      return null;
    }

    if (!isset(spec.fields) || !isarray(spec.fields)) {

      /* nothing we can do */

      this.lastError = `(columnMap) no schema fields in specification.`;

      return null;
    }

    for (const item of spec.fields) {

      if (!isset(item.name) || !isset(item.col)) {

        this.lastError = `(columnMap) a field name or column is missing in the fields spec.`;

        return null;
      }

      map.set(item.name, item.col - 1);
    }

    return map;
  }


  /** convert the given object to data table row, only properties that match field names will be created. */
  public toRow(object: any): any[] {

    if ( !this.cache()) {

      this.lastError = `(toRow) problems parsing JSON specification: ${this.lastError}`;

      /** unusable JSON specification? */

      return null;
    }

    const output: any[] = [];

    /*
     * sub-classes should implement how they convert, using columnMap to put the right field value
     * into the right column (the order).  To provide generic support, we loop through the field
     * specification and try to get those fields from the given object.
     *
     */

    /* try to map logical fields */

    const spec: Map<string, number> = this.columnMap();
    const cols: Map<number, any>    = new Map<number, any>();

    if ((spec === null) || (spec.size === 0)) {

      this.lastError = `(toRow) can not parse fields: ${this.lastError}`;

      return null;
    }

    /* map the field values to their columns */

    const props: string[] = Object.keys(object);
    let     max: number   = 0;

    for ( const [key, col] of spec) {

      /* does this object have 'key' as a property? */

      if (!props.includes(key)) {

        continue;
      }

      const value = object[key];

      cols.set(col, value);

      if (col >= max) {

        max = col;
      }
    }

    /* render the row (ordered fields */

    for (let i = 0; i <= max; i++) {

      if (!cols.has(i)) {

        output.push(null);

        continue;
      }

      output.push( cols.get(i) );
    }

    /* pass back the data row */

    return output;
  }

  /** convert the given set of objects to a complete data table */
  public dataTable(objects: any[]): any[] {

    if ( !this.cache()) {

      this.lastError = `(dataTable) problems parsing JSON specification: ${this.lastError}`;

      /** unusable JSON specification? */

      return null;
    }

    let output: any[] = [];

    for (const item of objects) {

      const row: any = this.toRow(item);

      if (row === null) {

        this.lastError = `(dataTable) problems converting objects: ${this.lastError}`;

        return null;
      }

      if (this.multiRow === true) {

        /* this object type generates 1..N rows per object */

        output = [].concat(output, row);

      } else {

        /* this object type only generates one row per object */

        output.push(row);
      }
    }

    /* pass it back */

    return output;
  }

}
